import React, { Suspense, lazy } from "react";
import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from 'react-helmet-async';

// Critical pages loaded immediately
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

// Lazy loaded pages - reduces initial bundle size
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const MoodCheckIn = lazy(() => import("@/pages/MoodCheckIn"));
const Exercises = lazy(() => import("@/pages/Exercises"));
const BreathingExercise = lazy(() => import("@/pages/BreathingExercise"));
const MeditationExercise = lazy(() => import("@/pages/MeditationExercise"));
const BodyScanExercise = lazy(() => import("@/pages/BodyScanExercise"));
const CBTTasks = lazy(() => import("@/pages/CBTTasks"));
const CBTHub = lazy(() => import("@/pages/CBTHub"));
const Chat = lazy(() => import("@/pages/Chat"));
const CrisisSupport = lazy(() => import("@/pages/CrisisSupport"));
const EmergencyContacts = lazy(() => import("@/pages/EmergencyContacts"));
const Profile = lazy(() => import("@/pages/Profile"));
const SleepCoach = lazy(() => import("@/pages/SleepCoach"));
const ExpertLearning = lazy(() => import("@/pages/ExpertLearning"));
const Community = lazy(() => import("@/pages/Community"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const CookiesPolicy = lazy(() => import("@/pages/CookiesPolicy"));

// Components
import CookieConsent from "@/components/CookieConsent";
const LocationConsentDialog = lazy(() => import("@/components/LocationConsentDialog"));
const SupportChatbot = lazy(() => import("@/components/SupportChatbot"));

// Auth Provider
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { GeolocationProvider } from "@/context/GeolocationContext";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 rounded-full gradient-sage animate-pulse-slow mx-auto mb-4" />
      <p className="text-[#718096] font-medium">Loading...</p>
    </div>
  </div>
);

// Protected Route Component - redirects to onboarding if not complete
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not complete (except for onboarding page itself)
  if (requireOnboarding && !user.onboarding_complete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  // Handle session_id from Google OAuth - check BEFORE any other routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/access" element={<AdminLogin />} />
        <Route path="/access/dashboard" element={<AdminDashboard />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiesPolicy />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Onboarding - protected but doesn't require onboarding completion */}
        <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><Onboarding /></ProtectedRoute>} />
        
        {/* Protected Routes - require onboarding completion */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/mood" element={<ProtectedRoute><MoodCheckIn /></ProtectedRoute>} />
        <Route path="/exercises" element={<ProtectedRoute><Exercises /></ProtectedRoute>} />
        <Route path="/exercises/breathing/:id" element={<ProtectedRoute><BreathingExercise /></ProtectedRoute>} />
        <Route path="/exercises/meditation/:id" element={<ProtectedRoute><MeditationExercise /></ProtectedRoute>} />
        <Route path="/exercises/body-scan/:id" element={<ProtectedRoute><BodyScanExercise /></ProtectedRoute>} />
        <Route path="/cbt-tasks" element={<ProtectedRoute><CBTTasks /></ProtectedRoute>} />
        <Route path="/cbt" element={<ProtectedRoute><CBTHub /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/crisis" element={<ProtectedRoute><CrisisSupport /></ProtectedRoute>} />
        <Route path="/contacts" element={<ProtectedRoute><EmergencyContacts /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/sleep" element={<ProtectedRoute><SleepCoach /></ProtectedRoute>} />
        <Route path="/learn" element={<ProtectedRoute><ExpertLearning /></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
        <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[var(--bg-primary)] dark:bg-[#1a1a2e] transition-colors duration-300">
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
              <GeolocationProvider>
                <AppRouter />
                <Toaster position="top-center" richColors />
                <CookieConsent />
                <Suspense fallback={null}>
                  <LocationConsentDialog />
                  <SupportChatbot />
                </Suspense>
              </GeolocationProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
